import type { Block, SectionBlock } from "@templatical/types";
import { isSection } from "@templatical/types";
import type { BlockRenderer, RenderContext } from "../render-context";
import { getWidthPercentages, getWidthPixels } from "../columns";
import { toPaddingString } from "../padding";
import { bgAttr, borderAttr, borderRadiusAttr } from "../utils";
import { isHiddenOnAll, getCssClassAttr } from "../visibility";
import { wrapWithDisplayCondition } from "../display-condition";

// `BlockRenderer` is defined in `../render-context`, next to the `blockRenderers`
// override map it types. Re-exported here so the package's
// `export type { BlockRenderer } from "./renderers/section"` entry point resolves
// for consumers.
export type { BlockRenderer };

/**
 * Render a section block with columns to MJML markup.
 */
export function renderSection(
  block: SectionBlock,
  context: RenderContext,
  renderBlock: BlockRenderer,
): string {
  if (isHiddenOnAll(block)) {
    return "";
  }

  const columnsLayout = block.columns;
  const columnWidths = getWidthPercentages(columnsLayout);
  const columnWidthsPx = getWidthPixels(columnsLayout, context.containerWidth);
  const padding = toPaddingString(block.styles.padding);
  const bgColor = bgAttr(block.styles.backgroundColor, "native");
  const visibilityAttr = getCssClassAttr(block);
  const borderRadiusAttrStr = borderRadiusAttr(block.borderRadius);
  const borderAttrStr = borderAttr(block.border);

  const children = block.children;
  const columnsContent: string[] = [];

  for (let index = 0; index < children.length; index++) {
    const column = children[index];
    const width = columnWidths[index] ?? "100%";
    const columnWidth = Math.floor(
      columnWidthsPx[index] ?? context.containerWidth,
    );

    // Sections cannot be nested inside columns per MJML spec — drop any
    // SectionBlocks that landed inside a column (via tampered JSON or a
    // future API). Keeping them would emit `<mj-section>` inside
    // `<mj-column>`, which mjml@5 rejects with a hard error.
    const filteredColumn = filterHtmlBlocks(
      column,
      context.allowHtmlBlocks,
    ).filter((child) => !isSection(child));
    const columnContext = context.withContainerWidth(columnWidth);

    const columnBlocks = filteredColumn
      .map((child) =>
        wrapWithDisplayCondition(child, renderBlock(child, columnContext)),
      )
      .filter((value) => value !== "")
      .join("\n");

    const content =
      columnBlocks === "" ? "<mj-text>&nbsp;</mj-text>" : columnBlocks;

    columnsContent.push(`<mj-column width="${width}">
${content}
</mj-column>`);
  }

  const columns = columnsContent.join("\n");

  // Opting out of mobile stacking wraps the columns in an `<mj-group>`, which
  // keeps them side-by-side below 480px instead of stacking (MJML's default).
  // `mj-group` requires percentage column widths — `getWidthPercentages`
  // already guarantees that. Only meaningful with 2+ columns.
  const dirAttr = context.contentDirection === "rtl" ? ' direction="rtl"' : "";

  const body =
    block.stackOnMobile === false && columnsContent.length > 1
      ? `<mj-group${dirAttr}>
${columns}
</mj-group>`
      : columns;

  return `<mj-section${bgColor} padding="${padding}"${borderAttrStr}${borderRadiusAttrStr}${visibilityAttr}${dirAttr}>
${body}
</mj-section>`;
}

/**
 * Filter out HTML blocks if they are not allowed.
 */
function filterHtmlBlocks(blocks: Block[], allowHtmlBlocks: boolean): Block[] {
  if (allowHtmlBlocks) {
    return blocks;
  }

  return blocks.filter((block) => block.type !== "html");
}
